/** Top panel filters for utilization insights using the shared unified wrapper. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Collapse, FormControl, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { FilterRequest } from '../../../types/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useSubBusOptions } from '../../../hooks/useSubBusOptions';
import { useDeliveryManagersOptions } from '../../../hooks/useDeliveryManagersOptions';
import { useSquadsOptions } from '../../../hooks/useSquadsOptions';
import { listContainsSbu, sortBusinessUnits } from '../../../utils/buUtils';
import { dashboardApi } from '../../../services/api';
import { CACHE_TIME } from '../../../constants/QueryConstants';
import MonthRangePicker from '../../MonthRangePicker';
import SelectAllMultiSelect, { FILTER_STANDARD } from '../../common/SelectAllMultiSelect';
import FilterStripLayout from '../FilterStripLayout';

const DELIVERY_BU_TYPE = 'Delivery';

interface UtilizationTopFiltersProps {
  filters: FilterRequest;
  onFiltersChange: (filters: FilterRequest) => void;
  onRefresh: () => void;
  hasUnsavedChanges?: boolean;
  elevation?: number;
}

/** Renders utilization page-specific filters and keeps existing defaults/API behavior. */
const UtilizationTopFilters: React.FC<UtilizationTopFiltersProps> = ({
  filters,
  onFiltersChange,
  onRefresh,
  hasUnsavedChanges = false,
}) => {
  const { user } = useAuth();
  const hasInitializedBUsRef = useRef(false);
  const hasInitializedSubBUsRef = useRef(false);
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);

  const deliveryBusinessUnitOptions = useMemo(() => {
    const list = user?.business_units?.filter((bu) => bu.bu_type === DELIVERY_BU_TYPE) ?? [];
    return list.map((bu) => bu.name);
  }, [user?.business_units]);

  const buOptionsLoading = !user;
  const sortedBusinessUnits = useMemo(() => sortBusinessUnits(deliveryBusinessUnitOptions), [deliveryBusinessUnitOptions]);
  const buOptions = useMemo(() => sortedBusinessUnits.map((bu) => ({ value: bu, label: bu })), [sortedBusinessUnits]);

  const { data: subBuOptions, isLoading: subBuLoading, error: subBuError } = useSubBusOptions(
    filters.business_units && filters.business_units.length > 0 ? filters.business_units : undefined
  );

  const buScope = filters.business_units && filters.business_units.length > 0 ? filters.business_units : undefined;
  const { data: deliveryManagerOptionsRaw, isLoading: dmLoading } = useDeliveryManagersOptions(buScope, filters.sub_bus);
  const { data: squadOptionsRaw, isLoading: squadLoading } = useSquadsOptions(buScope, filters.sub_bus, filters.delivery_managers);

  const dmOptions = useMemo(
    () => (deliveryManagerOptionsRaw || []).map((dm) => ({ value: dm.email, label: dm.name || dm.email })),
    [deliveryManagerOptionsRaw]
  );
  const dmValues = useMemo(() => dmOptions.map((o) => o.value), [dmOptions]);
  const dmOptionsKey = useMemo(() => dmValues.join(','), [dmValues]);

  const squadOptions = useMemo(
    () => (squadOptionsRaw || []).map((s) => ({ value: s, label: s })),
    [squadOptionsRaw]
  );
  const squadOptionsKey = useMemo(() => (squadOptionsRaw || []).join(','), [squadOptionsRaw]);

  const { data: filterOptions, isLoading: filterOptionsLoading } = useQuery({
    queryKey: ['utilization-people-filter-options'],
    queryFn: () => dashboardApi.getFilterOptions(['people']),
    staleTime: CACHE_TIME,
  });

  const availableSubBus = useMemo(
    () => (filters.business_units && filters.business_units.length > 0 ? subBuOptions : []),
    [filters.business_units, subBuOptions]
  );
  const sbuOptions = useMemo(() => availableSubBus.map((sbu) => ({ value: sbu, label: sbu })), [availableSubBus]);
  const availableSubBusKey = useMemo(() => availableSubBus.join(','), [availableSubBus]);
  const businessUnitsKey = useMemo(() => filters.business_units?.join(',') || '', [filters.business_units]);

  const getDefaultDuration = () => {
    const now = new Date();
    const yr = now.getFullYear();
    const mo = now.getMonth() + 1;
    let sy = yr, sm = 1;
    return { start_year: sy, start_month: sm, end_year: yr, end_month: mo };
  };

  useEffect(() => {
    if (hasInitializedBUsRef.current) return;
    if (deliveryBusinessUnitOptions.length > 0 && (!filters.business_units || filters.business_units.length === 0)) {
      onFiltersChange({
        ...filters,
        ...getDefaultDuration(),
        employee_status: filters.employee_status ?? 'ACTIVE',
        business_units: [...deliveryBusinessUnitOptions],
        sub_bus: [],
      });
      hasInitializedBUsRef.current = true;
    }
  }, [deliveryBusinessUnitOptions.join(','), filters.business_units?.length ?? 0]);

  useEffect(() => {
    if (hasInitializedSubBUsRef.current) return;
    if (filters.business_units && filters.business_units.length > 0 && (!filters.sub_bus || filters.sub_bus.length === 0) && !subBuLoading && availableSubBus.length > 0) {
      onFiltersChange({ ...filters, sub_bus: [...availableSubBus] });
      hasInitializedSubBUsRef.current = true;
    }
  }, [filters.business_units?.length ?? 0, filters.sub_bus?.length ?? 0, subBuLoading, availableSubBusKey]);

  useEffect(() => {
    if (filters.sub_bus && filters.sub_bus.length > 0 && !subBuLoading) {
      const validSubBUs = availableSubBus.filter((o) => listContainsSbu(filters.sub_bus || [], o));
      if (validSubBUs.length !== filters.sub_bus.length) onFiltersChange({ ...filters, sub_bus: validSubBUs });
    }
  }, [availableSubBusKey, businessUnitsKey, subBuLoading, filters.sub_bus]);

  // Drop stale Delivery Manager selections when the (cascaded) options change.
  useEffect(() => {
    if (filters.delivery_managers && filters.delivery_managers.length > 0 && !dmLoading) {
      const valid = filters.delivery_managers.filter((v) => dmValues.includes(v));
      if (valid.length !== filters.delivery_managers.length) onFiltersChange({ ...filters, delivery_managers: valid });
    }
  }, [dmOptionsKey, dmLoading, filters.delivery_managers]);

  // Drop stale Squad selections when the (cascaded) options change.
  useEffect(() => {
    if (filters.squads && filters.squads.length > 0 && !squadLoading) {
      const valid = filters.squads.filter((v) => (squadOptionsRaw || []).includes(v));
      if (valid.length !== filters.squads.length) onFiltersChange({ ...filters, squads: valid });
    }
  }, [squadOptionsKey, squadLoading, filters.squads]);

  const handleResetFilters = () => {
    hasInitializedSubBUsRef.current = false;
    onFiltersChange({
      ...getDefaultDuration(),
      employee_status: 'ACTIVE',
      business_units: [...deliveryBusinessUnitOptions],
      sub_bus: [],
      delivery_managers: [],
      squads: [],
      employee_names: [],
      managers: [],
      project_codes: [],
      project_status: [],
      designations: [],
      employee_types: [],
      countries: [],
      locations: [],
      pillars: [],
      work_mode_profiles: [],
    });
  };

  const canonicalSelectedSubBus = useMemo(
    () => availableSubBus.filter((o) => listContainsSbu(filters.sub_bus || [], o)),
    [availableSubBus, filters.sub_bus]
  );

  const buLoadingMsg = buOptionsLoading ? 'Loading...' : undefined;
  const sbuLoadingMsg = (buOptionsLoading || subBuLoading)
    ? (filters.business_units && filters.business_units.length > 0 ? 'Loading Sub-BUs...' : 'Loading...')
    : undefined;
  const sbuEmptyMsg = (filters.business_units && filters.business_units.length > 0)
    ? 'No Sub-BUs assigned to you for selected Business Units'
    : 'Select Business Units first';

  /**
   * Narrower fixed-max columns so labels don’t stretch edge-to-edge; gap comes from FilterStripLayout.
   * Slightly favor Duration with a bit more flex basis.
   */
  const colBase = {
    flex: '1 1 160px',
    minWidth: 112,
    maxWidth: 200,
    display: 'flex',
    alignItems: 'flex-end',
  } as const;
  const colDurationSx = { ...colBase, flex: '1.15 1 180px', maxWidth: 220 } as const;

  const toOptions = (values?: string[]) =>
    (values || []).filter(Boolean).map((value) => ({ value, label: value }));

  const additionalFilterDefs = [
    { key: 'employee_types', label: 'Employee Type', options: toOptions(filterOptions?.employee_types) },
    { key: 'countries', label: 'Country', options: toOptions(filterOptions?.countries) },
    { key: 'locations', label: 'Location', options: toOptions(filterOptions?.locations) },
    { key: 'pillars', label: 'Pillar', options: toOptions(filterOptions?.pillars) },
    { key: 'work_mode_profiles', label: 'Work Mode Profile', options: toOptions(filterOptions?.work_mode_profiles) },
  ] as const;

  return (
    <Box>
      <FilterStripLayout
        onClear={handleResetFilters}
        onRefresh={onRefresh}
        hasUnsavedChanges={hasUnsavedChanges}
        dense
        trailingActions={
          filterOptionsLoading ? (
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Loading filters...
            </Typography>
          ) : undefined
        }
        alert={
          subBuError && filters.business_units && filters.business_units.length > 0 ? (
            <Alert severity="warning" sx={{ mb: 0 }}>
              Failed to load Sub-BU options. Showing all available Sub-BUs.
            </Alert>
          ) : undefined
        }
      >
        <Box sx={colDurationSx}>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <MonthRangePicker
            startMonth={filters.start_year && filters.start_month ? { year: filters.start_year, month: filters.start_month } : null}
            endMonth={filters.end_year && filters.end_month ? { year: filters.end_year, month: filters.end_month } : null}
            onChange={(s, e) => onFiltersChange({ ...filters, start_year: s?.year, start_month: s?.month, end_year: e?.year, end_month: e?.month })}
            label="Duration"
          />
          </Box>
        </Box>
        <Box sx={colBase}>
          <Box sx={{ width: '100%', minWidth: 0 }}>
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: FILTER_STANDARD.fontSize }}>Employment Status</InputLabel>
            <Select
              value={filters.employee_status || 'ACTIVE'}
              onChange={(e) => onFiltersChange({ ...filters, employee_status: e.target.value as 'ACTIVE' | 'INACTIVE' | 'BOTH' })}
              label="Employment Status"
              sx={{ '& .MuiSelect-select': { fontSize: FILTER_STANDARD.fontSize } }}
            >
              <MenuItem value="ACTIVE">Active Only</MenuItem>
              <MenuItem value="INACTIVE">Inactive Only</MenuItem>
              <MenuItem value="BOTH" title="Include ACTIVE and INACTIVE employees (no Status filter in SQL)">
                Both Active & Inactive
              </MenuItem>
            </Select>
          </FormControl>
          </Box>
        </Box>
        <Box sx={colBase}>
          <Box sx={{ width: '100%', minWidth: 0 }}>
          <Tooltip title={!buOptionsLoading && deliveryBusinessUnitOptions.length === 0 ? 'No Business Units assigned. Contact admin.' : ''} placement="top" arrow>
            <Box sx={{ width: '100%' }}>
              <SelectAllMultiSelect
                label="Business Unit"
                options={buOptions}
                selectedValues={filters.business_units || []}
                onChange={(vals) => onFiltersChange({ ...filters, business_units: vals, sub_bus: [], delivery_managers: [], squads: [] })}
                disabled={buOptionsLoading || deliveryBusinessUnitOptions.length === 0}
                loadingMessage={buLoadingMsg}
                emptyMessage="No Business Units assigned to you"
                renderValue={(selected) => (
                  <Typography sx={{ fontSize: FILTER_STANDARD.fontSize, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                    {buOptionsLoading ? '' : deliveryBusinessUnitOptions.length === 0 ? 'No BUs assigned' : selected.length === 0 ? '' : `${selected.length} selected`}
                  </Typography>
                )}
              />
            </Box>
          </Tooltip>
          </Box>
        </Box>
        <Box sx={colBase}>
          <Box sx={{ width: '100%', minWidth: 0 }}>
          <Tooltip title={!subBuLoading && !buOptionsLoading && availableSubBus.length === 0 && filters.business_units && filters.business_units.length > 0 ? 'No Sub-BUs assigned for selected BUs.' : ''} placement="top" arrow>
            <Box sx={{ width: '100%' }}>
              <SelectAllMultiSelect
                label="Sub-BU"
                options={sbuOptions}
                selectedValues={canonicalSelectedSubBus}
                onChange={(vals) => onFiltersChange({ ...filters, sub_bus: vals, delivery_managers: [], squads: [] })}
                disabled={buOptionsLoading || subBuLoading || (availableSubBus.length === 0 && !!(filters.business_units && filters.business_units.length > 0))}
                loadingMessage={sbuLoadingMsg}
                emptyMessage={sbuEmptyMsg}
                renderValue={(selected) => {
                  const valid = selected.filter((i) => availableSubBus.includes(i));
                  return (
                    <Typography sx={{ fontSize: FILTER_STANDARD.fontSize, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                      {(buOptionsLoading || subBuLoading) ? '' : availableSubBus.length === 0 && filters.business_units?.length ? 'No SBUs assigned' : valid.length === 0 ? '' : `${valid.length} selected`}
                    </Typography>
                  );
                }}
              />
            </Box>
          </Tooltip>
          </Box>
        </Box>
        <Box sx={colBase}>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <SelectAllMultiSelect
              label="Delivery Manager"
              options={dmOptions}
              selectedValues={(filters.delivery_managers || []).filter((v) => dmValues.includes(v))}
              onChange={(vals) => onFiltersChange({ ...filters, delivery_managers: vals, squads: [] })}
              disabled={buOptionsLoading || dmLoading || (dmOptions.length === 0 && !!(filters.business_units && filters.business_units.length > 0))}
              loadingMessage={dmLoading ? 'Loading Delivery Managers...' : undefined}
              emptyMessage={filters.business_units && filters.business_units.length > 0 ? 'No Delivery Managers for selection' : 'Select Business Units first'}
              renderValue={(selected) => {
                const valid = selected.filter((i) => dmValues.includes(i));
                return (
                  <Typography sx={{ fontSize: FILTER_STANDARD.fontSize, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                    {dmLoading ? '' : valid.length === 0 ? '' : `${valid.length} selected`}
                  </Typography>
                );
              }}
            />
          </Box>
        </Box>
        <Box sx={colBase}>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <SelectAllMultiSelect
              label="Squad"
              options={squadOptions}
              selectedValues={(filters.squads || []).filter((v) => (squadOptionsRaw || []).includes(v))}
              onChange={(vals) => onFiltersChange({ ...filters, squads: vals })}
              disabled={buOptionsLoading || squadLoading || (squadOptions.length === 0 && !!(filters.business_units && filters.business_units.length > 0))}
              loadingMessage={squadLoading ? 'Loading Squads...' : undefined}
              emptyMessage={filters.business_units && filters.business_units.length > 0 ? 'No Squads for selection' : 'Select Business Units first'}
              renderValue={(selected) => {
                const valid = selected.filter((i) => (squadOptionsRaw || []).includes(i));
                return (
                  <Typography sx={{ fontSize: FILTER_STANDARD.fontSize, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                    {squadLoading ? '' : valid.length === 0 ? '' : `${valid.length} selected`}
                  </Typography>
                );
              }}
            />
          </Box>
        </Box>
      </FilterStripLayout>

      <Button
        size="small"
        endIcon={showAdditionalFilters ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        onClick={() => setShowAdditionalFilters((open) => !open)}
        sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
      >
        {showAdditionalFilters ? 'Hide Additional Filters' : 'Show Additional Filters'}
      </Button>

      <Collapse in={showAdditionalFilters} timeout="auto" unmountOnExit>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1, px: 0.5, pb: 0.5 }}>
          {additionalFilterDefs.map((def) => (
            <Box key={def.key} sx={colBase}>
              <Box sx={{ width: '100%', minWidth: 0 }}>
                <SelectAllMultiSelect
                  label={def.label}
                  options={def.options}
                  selectedValues={(filters[def.key] as string[] | undefined) || []}
                  onChange={(vals) => onFiltersChange({ ...filters, [def.key]: vals })}
                  disabled={filterOptionsLoading}
                  loadingMessage={filterOptionsLoading ? 'Loading...' : undefined}
                  emptyMessage={`No ${def.label} options`}
                  renderValue={(selected) => (
                    <Typography sx={{ fontSize: FILTER_STANDARD.fontSize, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                      {filterOptionsLoading ? '' : selected.length === 0 ? '' : `${selected.length} selected`}
                    </Typography>
                  )}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default UtilizationTopFilters;

